import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, LogOut, ArrowRight, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminNav from '../components/AdminNav';
import { templateService, authService } from '../services/api';

function AdminTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', format: '', size: '', downloadUrl: '' });

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await templateService.getAll();
        if (response.data) {
          setTemplates(response.data);
        }
      } catch (err) {
        setError('خطا در دریافت قالب‌ها');
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let updated;
      if (editingId) {
        const response = await templateService.update(editingId, formData);
        updated = templates.map(t => t.id === editingId ? response.data : t);
      } else {
        const response = await templateService.create(formData);
        updated = [...templates, response.data];
      }
      setTemplates(updated);
      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', description: '', format: '', size: '', downloadUrl: '' });
      setError('');
    } catch (err) {
      setError('خطا در ذخیره قالب');
    }
  };

  const handleEdit = (template) => {
    setFormData({
      name: template.name,
      description: template.description,
      format: template.format,
      size: template.size,
      downloadUrl: template.downloadUrl,
    });
    setEditingId(template.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا مطمئن هستید؟')) return;
    try {
      await templateService.delete(id);
      setTemplates(templates.filter(t => t.id !== id));
    } catch (err) {
      setError('خطا در حذف قالب');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ name: '', description: '', format: '', size: '', downloadUrl: '' });
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">در حال بارگذاری...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">مدیریت قالب و الگو</h1>
              <p className="text-xl text-blue-100">ویرایش قالب‌ها و الگوهای نشریه</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigate('/admin/articles')} className="flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-600 rounded-lg font-semibold transition">
                <ArrowRight size={18} />
                بازگشت
              </button>
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition">
                <LogOut size={18} />
                خروج
              </button>
            </div>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">{error}</div>}

        {!showForm && (
          <button onClick={() => setShowForm(true)} className="mb-8 flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-bold hover:from-green-700 hover:to-green-800 transition shadow-lg">
            <Plus size={20} />
            افزودن قالب جدید
          </button>
        )}

        {showForm && (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">{editingId ? 'ویرایش قالب' : 'افزودن قالب جدید'}</h2>
              <button type="button" onClick={handleCancel} className="p-2 hover:bg-gray-200 rounded-lg transition">
                <X size={24} className="text-gray-600" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-900 mb-2">نام قالب</label>
                <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-900 mb-2">توضیحات</label>
                <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} required rows="3" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">فرمت</label>
                <input type="text" value={formData.format} onChange={e => setFormData({ ...formData, format: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">حجم</label>
                <input type="text" value={formData.size} onChange={e => setFormData({ ...formData, size: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-900 mb-2">لینک دانلود</label>
                <input type="text" value={formData.downloadUrl} onChange={e => setFormData({ ...formData, downloadUrl: e.target.value })} className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button type="submit" className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition">ذخیره</button>
              <button type="button" onClick={handleCancel} className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition">انصراف</button>
            </div>
          </form>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.length ? templates.map(template => (
            <div key={template.id} className="bg-white rounded-xl shadow-md p-6 flex flex-col">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-blue-700" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">{template.name}</h3>
                  <p className="text-sm text-gray-500">{template.format}</p>
                </div>
              </div>
              <p className="text-gray-600 text-sm mb-4 flex-1">{template.description}</p>
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <span className="text-xs text-gray-500">{template.size}</span>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(template)} className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"><Edit2 size={16} /></button>
                  <button onClick={() => handleDelete(template.id)} className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          )) : (
            <div className="col-span-full bg-white rounded-xl shadow-md p-8 text-center text-gray-500">
              <p>هیچ قالبی برای نمایش وجود ندارد</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminTemplates;
